import {css, StyleSheet} from 'aphrodite';
import {noop} from 'jquery';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import ErrorTriangle, {TriangleLocation, TriangleState} from 'notebook/tables/errors/ErrorTriangle';
import {Cell, ColumnConstraint, Table} from 'notebook/tables/TableConstants';
import notifications from 'notifications';
import fillArray from 'procedures/planning/fillArray';
import {ProcedureExperimentRowLabel} from 'procedures/planning/ProcedureExperimentRowLabel';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {TimingEstimateResult} from 'studies/progress/useTimingEstimate';
import {EXPERIMENT_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import {ProcedureRunConditionsSummary} from 'studies/typed/process-development/ProcedureRunConditionsSummary';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {NEAR_BLACK} from 'ui-library/Colors';
import * as modals from 'util/Modals';
import {Optional} from 'util/Types';

import {getConditionCountsByName} from './ExperimentDesignEditor';
import {Condition, ConditionValidationState} from './useProcessDevelopmentStudyState';

interface PDColumnConstraint extends ColumnConstraint {
  processDevelopmentCustomIndex: number;
}

function makeInitialReplicateTable(
  tableName: string,
  replicatesPerCondition: Array<number>,
  namesPerCondition: Array<string>,
  descriptionsPerCondition: Array<string | undefined>,
  validationStatePerCondition: Array<ConditionValidationState>,
  isLockedPerCondition: Array<boolean>,
  locked: boolean,
  procedureType: ProcedureType
): Table {
  const makeValueCell = (value: number, index: number): Cell => ({
    text: value.toString(),
    locked: locked || isLockedPerCondition[index],
    align: 'right',
    foregroundColor: NEAR_BLACK,
    bold: false,
  });

  const getNameErrorTriangle = (index: number): Optional<JSX.Element> => {
    if (validationStatePerCondition[index].isNameLengthOverLimit) {
      return (
        <ErrorTriangle
          state={TriangleState.ILLEGAL}
          error={{
            type: '',
            message: `${getProcedureRunConditionTextForProcedureType(procedureType, {
              capital: true,
            })} name may not exceed ${EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH} characters`,
          }}
          location={TriangleLocation.CELL}
        />
      );
    } else if (validationStatePerCondition[index].isDuplicateName) {
      return (
        <ErrorTriangle
          state={TriangleState.ILLEGAL}
          error={{
            type: '',
            message: `${getProcedureRunConditionTextForProcedureType(procedureType, {
              capital: true,
            })} name already in use`,
          }}
          location={TriangleLocation.CELL}
        />
      );
    } else {
      return undefined;
    }
  };

  const getDescErrorTriangle = (index: number): Optional<JSX.Element> => {
    if (validationStatePerCondition[index].isDescLengthOverLimit) {
      return (
        <ErrorTriangle
          state={TriangleState.ILLEGAL}
          error={{
            type: '',
            message: `${getProcedureRunConditionTextForProcedureType(procedureType, {
              capital: true,
            })} description may not exceed ${
              EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_DESCRIPTION_MAX_LENGTH
            } characters`,
          }}
          location={TriangleLocation.CELL}
        />
      );
    } else {
      return undefined;
    }
  };

  const makeNameCell = (text: string, index: number): Cell => ({
    text,
    locked: locked || isLockedPerCondition[index],
    align: 'right',
    bold: false,
    userAnnotationIndicator: getNameErrorTriangle(index),
  });

  const makeDescCell = (text: string, index: number): Cell => ({
    text,
    locked: locked || isLockedPerCondition[index],
    align: 'right',
    bold: false,
    userAnnotationIndicator: getDescErrorTriangle(index),
  });

  const numConditions = replicatesPerCondition.length;
  return {
    name: tableName,
    _firstColumnWidth: 170,
    widths: replicatesPerCondition.map(() => 120),
    rowLabels: [
      <ProcedureExperimentRowLabel
        name={`${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} name`}
      />,
      <ProcedureExperimentRowLabel name="Description" />,
      <ProcedureExperimentRowLabel name="Number of replicates" />,
    ],
    columnLabels: fillArray(
      numConditions,
      (conditionNumber) =>
        `${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} ${
          conditionNumber + 1
        }`
    ),
    columnConstraints: replicatesPerCondition.map(
      (_numReplicates, conditionIdx): PDColumnConstraint => ({
        processDevelopmentCustomIndex: conditionIdx,
        editLabelDisabled: true,
      })
    ),
    headerCol: false,
    headerRow: false,
    table: [
      fillArray(numConditions, (conditionIndex) =>
        makeNameCell(namesPerCondition[conditionIndex], conditionIndex)
      ),
      fillArray(numConditions, (conditionIndex) =>
        makeDescCell(descriptionsPerCondition[conditionIndex] ?? '', conditionIndex)
      ),
      fillArray(numConditions, (conditionIndex) =>
        makeValueCell(replicatesPerCondition[conditionIndex], conditionIndex)
      ),
    ],
    lockedDims: {allowAddRows: false, allowRemoveRows: false, allowEditCols: !locked},
    allowColumnEdits: !locked,
    _hideRename: true,
  };
}

function getReplicateCellValue(cell: Cell): number | null {
  if (cell.text == null) {
    return null;
  }
  const value = Number.parseInt(cell.text, 10);
  if (Number.isNaN(value) || value <= 0) {
    notifications.error(
      `The number of replicates was reset to 1 since the specified value was not a whole number greater than 0`,
      {id: 'ReplicateTable-replicate-bounds'}
    );
    return 1;
  }
  return value;
}

/**
 *  Update conditions when any individual cell is updated.
 */
function updateTableCells(
  table: Table,
  conditions: Array<Condition>,
  setConditions: (conditions: Array<Condition>, prevIndexes: Array<number | null>) => void,
  procedureType: ProcedureType
): void {
  const originalValueIndexes = table.columnConstraints.map(
    (c) => (c as PDColumnConstraint)?.processDevelopmentCustomIndex ?? null
  );

  // assigns newly added column cells (which have a originalValueIndex == null) to have a cell.text = '1'
  const newConditions = table.table[2].map((cell, index) => ({
    numReplicates:
      getReplicateCellValue({...cell, text: originalValueIndexes[index] == null ? '1' : cell.text}) ?? 1,
    name:
      table.table[0][index].text == null || table.table[0][index].text === ''
        ? `${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} ${index + 1}`
        : table.table[0][index].text,
    description: table.table[1][index].text,
  }));
  const newConditionNames = newConditions.map((condition) => condition.name);
  const conditionCounts = getConditionCountsByName(newConditionNames);
  setConditions(
    [
      ...newConditions.map((condition, i) => ({
        ...condition,
        id: originalValueIndexes[i] != null ? conditions[originalValueIndexes[i]]?.id : undefined,
        validationState: {
          isDuplicateName: conditionCounts.get(condition.name)! > 1,
          isNameLengthOverLimit:
            condition.name.length > EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH,
          isDescLengthOverLimit:
            (condition.description ?? '').length >
            EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_DESCRIPTION_MAX_LENGTH,
        },
      })),
    ],
    originalValueIndexes
  );
}

export function ReplicateTable(props: {
  conditions: Array<Condition>;
  setConditions: (conditions: Array<Condition>, prevIndexes: Array<number | null>) => void;
  selectedMethodId: string;
  experimentalInputs: ExperimentalInputs | null;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  editNumColsButtons: JSX.Element;
  locked: boolean;
  estimate: TimingEstimateResult;
  procedureType: ProcedureType;
  dataTestAttrs?: {[key: string]: string};
}): JSX.Element {
  const table = makeInitialReplicateTable(
    `${getProcedureRunConditionTextForProcedureType(props.procedureType, {capital: true})} settings`,
    props.conditions.map((item) => item.numReplicates),
    props.conditions.map((item) => item.name),
    props.conditions.map((item) => item.description),
    props.conditions.map(
      (item) =>
        item.validationState ?? {
          isDuplicateName: false,
          isNameLengthOverLimit: false,
          isDescLengthOverLimit: false,
        }
    ),
    // Persisted conditions are locked
    props.conditions.map((item) => item.id != null),
    props.locked,
    props.procedureType
  );

  const conditionsSummary = (
    <ProcedureRunConditionsSummary
      conditions={props.conditions}
      estimate={props.estimate}
      procedureType={props.procedureType}
    />
  );

  const leftHeaderControls = !props.locked ? props.editNumColsButtons : undefined;

  function confirmDelete(
    index: number,
    start: number,
    end: number,
    doDelete: (index: number, start: number, end: number) => void
  ): void {
    const numConditions = props.conditions.length;
    const numConditionsToRemove = end - start + 1;
    const singleConditionText = getProcedureRunConditionTextForProcedureType(props.procedureType);
    const conditionsText = pluralize(numConditionsToRemove, singleConditionText);
    const columnsText = pluralize(numConditionsToRemove, 'column');
    const numUnitOps = Object.keys(props.experimentalInputs ?? {}).length;

    modals.confirm(
      `Remove ${conditionsText}?`,
      <>
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={`${numConditionsToRemove} ${singleConditionText} ${columnsText} across ${numUnitOps} unit ${pluralize(
            numUnitOps,
            'operation'
          )} will be lost`}
        />
        <div className="u-small-spacing-top">
          {`You're about to update your ${`${singleConditionText}s`} from ${numConditions} per unit operation to ${
            numConditions - numConditionsToRemove
          } per unit operation. That means ${numConditionsToRemove} filled ${columnsText} will be deleted.`}
        </div>
      </>,
      ButtonStyle.NEGATIVE,
      {
        acceptText: `Remove ${conditionsText}`,
        rejectText: 'Back to editing',
        enableRedesign: true,
        onSubmit: () => {
          doDelete(index, start, end);
        },
      }
    );
  }

  return (
    <div className="u-flex-display mediocre">
      <div className={css(styles.maxWidth)}>
        <SimpleTableEditable
          {...standaloneProps}
          hideFormulaBox={true}
          isCompactBottomSpacing={true}
          isLeftAlignRowHeader={true}
          locked={props.locked}
          table={table}
          cid="doe-replicates"
          updateHandler={(_, {table}: {table: Table}) => {
            updateTableCells(table, props.conditions, props.setConditions, props.procedureType);
          }}
          storeState={noop}
          restoreState={noop}
          isCollapsible={true}
          leftHeaderControls={leftHeaderControls}
          headerControls={conditionsSummary}
          hideRowCount={true}
          onDeleteColumns={confirmDelete}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  maxWidth: {
    width: '100%',
    paddingRight: '24px',
  },
});
